import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FaPlus } from 'react-icons/fa';
import { useAbility } from '../../../contexts/AbilityContext';
import { useAlert } from '../../../contexts/AlertContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import EmployeeSearch from '../../common/EmployeeSearch';
import { HeaderButton } from '../../common/styles';
import { feedbackApi, organizationApi } from '../../../services/api';
import { skillsApi } from '../../../services/api/skillsApi';
import ReviewerAssignmentDialog from '../common/ReviewerAssignmentDialog';
import SkillsMatrixSelectionList from '../common/SkillsMatrixSelectionList';
import { canReopenReview, canResetReview, canReopenReviewerActions, ASSIGNMENT_STATUSES } from '../../../constants/assignmentStatuses';
import { REVIEW_TYPES } from '../../../constants/reviewTypes';

/**
 * ADR-003 follow-up: a Sequential reviewer step can be reordered while its review
 * has NOT STARTED — startedAt/completedAt/feedbackAttachedAt are all unset. This
 * mirrors the backend `isReorderableNotStartedReviewer` guard (the server stays
 * authoritative). It includes the currently-active step (assignedAt set but not
 * started): the server swaps `assignedAt` together with `order`, so the active
 * pointer follows whichever reviewer ends up in the active slot. The self-
 * assessment is excluded separately (it must stay first).
 */
const isReorderableNotStartedReviewer = (reviewer) =>
  reviewer?.reviewType === REVIEW_TYPES.SEQUENTIAL &&
  !reviewer.startedAt &&
  !reviewer.completedAt &&
  !reviewer.feedbackAttachedAt;

/**
 * Find the assignee's team within a fetched BU hierarchy so the shared dialog
 * can offer the "Assign Team Members" shortcut on the report. Mirrors the
 * resolution used by the feedback-session setup flow.
 */
const findEmployeeTeam = (data, employeeId) => {
  const searchTeams = (teams) => {
    if (!teams) {
      return null;
    }
    for (const team of teams) {
      if (team.employees?.some((emp) => emp.id === employeeId)) {
        return team;
      }
      if (team.childTeams) {
        const found = searchTeams(team.childTeams);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };
  for (const project of data?.projects || []) {
    const found = searchTeams(project.teams);
    if (found) {
      return found;
    }
  }
  return null;
};

/**
 * ADR-003 Action Item 7 — modification affordances on the feedback-session report page.
 *
 * Authorization (ADR-003 phase 2) is the FeedbackSessionModification subject,
 * granted as a single `manage` action; these components render nothing without
 * it (the server enforces the same gate on every modification route). They are
 * intentionally self-contained: each owns its menu, modals, API calls, and error
 * handling, and calls `onChanged()` after a successful mutation so the host page
 * can reload.
 */

const useCanModifyFeedbackSession = () => {
  const ability = useAbility();
  return ability.can(ACTIONS.MANAGE, SUBJECTS.FEEDBACK_SESSION_MODIFICATION);
};

/**
 * Matrices an operator may pick from when *changing* an assignee's matrix.
 *
 * Mirrors the edit-employees "edit matrix" (override) affordance: the choices
 * are scoped to the matrices configured on the feedback session's template —
 * the same `getSkillsMatrixTemplatesBySessionId` source the setup flow uses —
 * with no per-assignee attribute filtering. Roles/versions are derived from
 * those templates so the cards match the setup flow exactly.
 *
 * Individual assignments carry their session template on the review process
 * (surfaced as `sessionTemplate` by the individual report); only legacy rows
 * without one fall back to all matrices.
 */
const useSessionMatrixOptions = (open, sessionTemplateId) => {
  const [matrices, setMatrices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let active = true;
    setLoading(true);

    if (!sessionTemplateId) {
      skillsApi
        .getAllSkillsMatrices()
        .then((list) => active && setMatrices(Array.isArray(list) ? list : []))
        .catch(() => active && setMatrices([]))
        .finally(() => active && setLoading(false));
      setTemplates([]);
      return () => {
        active = false;
      };
    }

    skillsApi
      .getSkillsMatrixTemplatesBySessionId(sessionTemplateId)
      .then((tpls) => {
        if (!active) {
          return;
        }
        const list = Array.isArray(tpls) ? tpls : [];
        setTemplates(list);
        const matricesMap = new Map();
        list.forEach((template) => {
          if (template.skillsMatrix && !matricesMap.has(template.skillsMatrix.id)) {
            matricesMap.set(template.skillsMatrix.id, template.skillsMatrix);
          }
        });
        setMatrices(Array.from(matricesMap.values()));
      })
      .catch(() => {
        if (active) {
          setMatrices([]);
          setTemplates([]);
        }
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [open, sessionTemplateId]);

  // Derive review-type/version chips from the session templates (matches the
  // setup flow). When templates aren't available (individual-assignment
  // fallback), return null so the list derives roles from each matrix instead.
  const getMatrixRoles =
    templates.length > 0
      ? (matrix) => {
          const rolesMap = new Map();
          templates
            .filter((t) => t.skillsMatrix && t.skillsMatrix.id === matrix.id)
            .forEach((t) => {
              if (t.type) {
                const key = `${t.type}-${t.version || 'no-version'}`;
                if (!rolesMap.has(key)) {
                  rolesMap.set(key, { type: t.type, version: t.version });
                }
              }
            });
          return Array.from(rolesMap.values());
        }
      : null;

  return { matrices, getMatrixRoles, loading };
};

// --- Decision 1: Remove assignee --------------------------------------------
const RemoveAssigneeModal = ({ open, onClose, sessionId, assignmentId, assigneeName, onChanged }) => {
  const { showAlert } = useAlert();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return;
    }
    setSubmitting(true);
    try {
      await feedbackApi.removeAssignment(sessionId, assignmentId, reason.trim());
      showAlert('Assignment removed', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      showAlert(err.message || 'Failed to remove assignment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove {assigneeName || 'assignee'} from this session</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This permanently deletes the assignment and all of its reviewers and responses. This
          cannot be undone (an audit record is kept).
        </Alert>
        <TextField
          label="Reason (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={submitting || !reason.trim()}>
          {submitting ? <CircularProgress size={18} /> : 'Remove assignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 3: Change matrix (preview + confirm) --------------------------
const ChangeMatrixModal = ({ open, onClose, assignmentId, sessionTemplateId, onChanged }) => {
  const { showAlert } = useAlert();
  const { matrices, getMatrixRoles, loading: matricesLoading } = useSessionMatrixOptions(
    open,
    sessionTemplateId
  );
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [newSkillsMatrixId, setNewSkillsMatrixId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setNewSkillsMatrixId('');
      setReason('');
      return;
    }
    let active = true;
    setPreviewLoading(true);
    skillsApi
      .getMatrixChangePreview(assignmentId)
      .then((p) => active && setPreview(p))
      .catch((err) => active && showAlert(err.message || 'Failed to load preview', 'error'))
      .finally(() => active && setPreviewLoading(false));
    return () => {
      active = false;
    };
  }, [open, assignmentId, showAlert]);

  const blocked = preview?.submittedFoDBlockers?.length > 0;

  const handleConfirm = async () => {
    if (!newSkillsMatrixId || !reason.trim() || !preview) {
      return;
    }
    setSubmitting(true);
    try {
      await skillsApi.changeMatrix(assignmentId, {
        newSkillsMatrixId,
        reason: reason.trim(),
        confirmedDiscardedFoDCount: preview.discardedFoDCount,
        confirmedResponseCount: preview.responseCount,
      });
      showAlert('Skills matrix changed', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      // A 409 may carry refreshed counts — surface and let the operator retry.
      if (err.status === 409 && err.data) {
        setPreview((prev) => ({
          ...prev,
          discardedFoDCount: err.data.currentDiscardedFoDCount ?? prev?.discardedFoDCount,
          responseCount: err.data.currentResponseCount ?? prev?.responseCount,
          submittedFoDBlockers: err.data.submittedFoDBlockers ?? prev?.submittedFoDBlockers,
        }));
      }
      showAlert(err.message || 'Failed to change matrix', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change skills matrix</DialogTitle>
      <DialogContent>
        {previewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            {blocked && (
              <Alert severity="error" sx={{ mb: 2 }}>
                This assignment has submitted Start/Stop/Continue feedback and cannot have its matrix
                changed until that is reverted (Reopen SSC).
              </Alert>
            )}
            {preview && !blocked && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Changing the matrix will delete {preview.discardedFoDCount} draft Start/Stop/Continue
                feedback record(s) and wipe {preview.responseCount} response(s) across all reviewers.
                This cannot be undone.
              </Alert>
            )}
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2">New skills matrix</Typography>
              {!matricesLoading && matrices.length > 0 && (
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                  {matrices.length} {matrices.length === 1 ? 'matrix' : 'matrices'} available — scroll to see all
                </Typography>
              )}
            </Box>
            {matricesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : (
              <Box
                sx={{
                  maxHeight: 340,
                  overflowY: 'auto',
                  pr: 1,
                  opacity: blocked ? 0.5 : 1,
                  pointerEvents: blocked ? 'none' : 'auto',
                }}
              >
                <SkillsMatrixSelectionList
                  matrices={matrices}
                  selectedMatrixId={newSkillsMatrixId}
                  onSelect={(matrix) => setNewSkillsMatrixId(matrix.id)}
                  {...(getMatrixRoles ? { getMatrixRoles } : {})}
                  emptyDescription="There are no skills matrices configured for this session."
                />
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <TextField
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              required
              disabled={blocked}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || blocked || !newSkillsMatrixId || !reason.trim()}
        >
          {submitting ? <CircularProgress size={18} /> : 'Change matrix'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 5: Reopen Start/Stop/Continue ---------------------------------
const ReopenActionsModal = ({ open, onClose, assignmentId, onChanged }) => {
  const { showAlert } = useAlert();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await skillsApi.reopenFeedbackActions(assignmentId, reason.trim() || undefined);
      showAlert('Start/Stop/Continue reopened', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      showAlert(err.message || 'Failed to reopen actions', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reopen Start/Stop/Continue</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This reverts the submitted Start/Stop/Continue feedback to a draft so the representative can
          edit and resubmit it. The text is preserved.
        </Typography>
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : 'Reopen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 7: Add reviewer -----------------------------------------------
// Reuses the same dialog as the feedback-session setup flow (edit-reviewers):
// consultant search + template picker filtered to the assignee's skills matrix,
// plus the "Assign Team Members" shortcut for parallel reviews. The selected
// template is persisted so the new reviewer matches reviewers created at setup.
const AddReviewerModal = ({ open, onClose, sessionTemplateId, businessUnitId, assignment, reviewType, onChanged }) => {
  const { showAlert } = useAlert();
  const [templates, setTemplates] = useState([]);
  const [team, setTeam] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const assignmentId = assignment?.assignment?.id;
  const assignee = assignment?.employee;
  const matrix = assignment?.matrix;
  const matrixId = matrix?.id;
  const existingReviewers = assignment?.reviewers || [];

  // On open: load the templates the dialog filters by matrix, and resolve the
  // assignee's team (for the bulk shortcut). Templates come from the session's
  // skills-matrix-session — the same source as the edit-employees flow — so the
  // versions match the reviewers already on the session. Individual assignments
  // carry theirs on the review process (ADR-106 follow-up); only legacy rows
  // without one fall back to the matrix's templates (all versions). Both are
  // best-effort — failure simply hides the grid / team button.
  useEffect(() => {
    if (!open) {
      setTemplates([]);
      setTeam(null);
      return undefined;
    }
    let active = true;
    const templatesPromise = sessionTemplateId
      ? skillsApi.getSkillsMatrixTemplatesBySessionId(sessionTemplateId)
      : matrixId
      ? skillsApi.getTemplatesByMatrixId(matrixId)
      : Promise.resolve([]);
    templatesPromise
      .then((t) => active && setTemplates(Array.isArray(t) ? t : []))
      .catch(() => active && setTemplates([]));
    if (businessUnitId && assignee?.id) {
      organizationApi
        .getBusinessUnitDetailedHierarchy(businessUnitId)
        .then((data) => active && setTeam(findEmployeeTeam(data, assignee.id)))
        .catch(() => active && setTeam(null));
    }
    return () => {
      active = false;
    };
  }, [open, sessionTemplateId, matrixId, businessUnitId, assignee?.id]);

  const handleConfirm = async ({ reviewer, template }) => {
    if (!assignmentId || !reviewer?.id || !template?.id) {
      return;
    }
    setSubmitting(true);
    try {
      await skillsApi.addReviewer(assignmentId, {
        employeeId: reviewer.id,
        skillsMatrixTemplateId: template.id,
      });
      showAlert('Reviewer added', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      showAlert(err.message || 'Failed to add reviewer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignTeam = async (reviewerAssignments) => {
    if (!assignmentId) {
      return;
    }
    // Skip the assignee (self-review already exists / banned) and anyone already
    // reviewing this assignee, so the team shortcut is additive only.
    const existingIds = new Set(existingReviewers.map((r) => r.reviewer.id));
    const reviewers = (reviewerAssignments || [])
      .filter((ra) => ra.reviewer.id !== assignee?.id && !existingIds.has(ra.reviewer.id))
      .map((ra) => ({ employeeId: ra.reviewer.id, skillsMatrixTemplateId: ra.template.id }));

    if (reviewers.length === 0) {
      showAlert('No new team members to add as reviewers', 'info');
      return;
    }
    setSubmitting(true);
    try {
      const result = await skillsApi.bulkAddReviewers(assignmentId, { reviewers });
      const skippedNote = result?.skippedCount ? `, skipped ${result.skippedCount}` : '';
      showAlert(`Added ${result?.addedCount ?? reviewers.length} reviewer(s)${skippedNote}`, 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      showAlert(err.message || 'Failed to add reviewers', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ReviewerAssignmentDialog
      open={open}
      onClose={submitting ? () => {} : onClose}
      employeeToReview={assignee}
      existingReviewers={existingReviewers}
      matrix={matrix}
      templates={templates}
      reviewType={reviewType}
      team={team}
      onConfirm={handleConfirm}
      onAssignTeam={handleAssignTeam}
      submitting={submitting}
    />
  );
};

// --- Assignee-level admin actions (D1, D3, D5, D7) --------------------------
export const AssigneeAdminActions = ({
  sessionId,
  assignmentId,
  assigneeName,
  reviewType,
  assignment,
  assignmentStatus,
  businessUnitId,
  sessionTemplateId,
  isIndividualAssignment = false,
  // ADR-014 §7: true when the parent session is closed (snapshot close) — the
  // frozen session offers no modification actions (the backend rejects them too).
  sessionLocked = false,
  onChanged,
}) => {
  const canModify = useCanModifyFeedbackSession();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeModal, setActiveModal] = useState(null);

  if (!canModify || sessionLocked) {
    return null;
  }

  const openModal = (name) => {
    setActiveModal(name);
    setAnchorEl(null);
  };

  // ADR-003 follow-up: Start/Stop/Continue can only be reopened once the
  // assignment is fully COMPLETED (i.e. the representative has submitted their
  // SSC actions). This mirrors the backend `reopenActions` precondition, which
  // rejects anything other than a SUBMITTED FeedbackOnDemand.
  const canReopenActions = assignmentStatus === ASSIGNMENT_STATUSES.COMPLETED;

  // ADR-003 follow-up: a COMPLETED assignment is locked for structural changes —
  // adding a reviewer or changing the skills matrix would wipe completed work.
  // Mirrors the backend `addReviewerTx` / `changeMatrix` guards (the server stays
  // authoritative). Reopen the assignment first.
  const assignmentCompleted = assignmentStatus === ASSIGNMENT_STATUSES.COMPLETED;
  const completedAssignmentDisabledReason =
    'The assignment is completed. Reopen it (Reopen Start/Stop/Continue, then reopen the reviews) before adding a reviewer or changing the matrix.';

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Admin actions">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <Tooltip title={assignmentCompleted ? completedAssignmentDisabledReason : ''} placement="left">
          <span>
            <MenuItem onClick={() => openModal('addReviewer')} disabled={assignmentCompleted}>
              Add reviewer
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={assignmentCompleted ? completedAssignmentDisabledReason : ''} placement="left">
          <span>
            <MenuItem onClick={() => openModal('changeMatrix')} disabled={assignmentCompleted}>
              Change matrix
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={
            canReopenActions
              ? ''
              : 'Start/Stop/Continue can only be reopened once the assignment is Completed'
          }
          placement="left"
        >
          {/* span keeps the Tooltip working while the MenuItem is disabled */}
          <span>
            <MenuItem
              onClick={() => openModal('reopenActions')}
              disabled={!canReopenActions}
            >
              Reopen Start/Stop/Continue
            </MenuItem>
          </span>
        </Tooltip>
        {/* Decision 1 is session-scoped; hidden in individual-assignment mode. */}
        {!isIndividualAssignment && (
          <MenuItem onClick={() => openModal('removeAssignee')} sx={{ color: 'error.main' }}>
            Remove assignee
          </MenuItem>
        )}
      </Menu>

      <AddReviewerModal
        open={activeModal === 'addReviewer'}
        onClose={() => setActiveModal(null)}
        sessionTemplateId={sessionTemplateId}
        businessUnitId={businessUnitId}
        assignment={assignment}
        reviewType={reviewType}
        onChanged={onChanged}
      />
      <ChangeMatrixModal
        open={activeModal === 'changeMatrix'}
        onClose={() => setActiveModal(null)}
        assignmentId={assignmentId}
        sessionTemplateId={sessionTemplateId}
        onChanged={onChanged}
      />
      <ReopenActionsModal
        open={activeModal === 'reopenActions'}
        onClose={() => setActiveModal(null)}
        assignmentId={assignmentId}
        onChanged={onChanged}
      />
      {!isIndividualAssignment && (
        <RemoveAssigneeModal
          open={activeModal === 'removeAssignee'}
          onClose={() => setActiveModal(null)}
          sessionId={sessionId}
          assignmentId={assignmentId}
          assigneeName={assigneeName}
          onChanged={onChanged}
        />
      )}
    </>
  );
};

// --- Decision 2: Replace reviewer (transfer / wipe) -------------------------
const ReplaceReviewerModal = ({ open, onClose, assignmentId, reviewerId, onChanged }) => {
  const { showAlert } = useAlert();
  const [newEmployee, setNewEmployee] = useState(null);
  const [reason, setReason] = useState('');
  const [confirmTransfer, setConfirmTransfer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setNewEmployee(null);
    setReason('');
    setConfirmTransfer(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (keepResponses) => {
    if (!newEmployee?.id) {
      return;
    }
    if (!keepResponses && !reason.trim()) {
      return;
    }
    setSubmitting(true);
    try {
      await skillsApi.replaceReviewer(assignmentId, reviewerId, {
        newEmployeeId: newEmployee.id,
        keepResponses,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      showAlert('Reviewer replaced', 'success');
      onChanged?.();
      close();
    } catch (err) {
      showAlert(err.message || 'Failed to replace reviewer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Replace reviewer</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, mb: 2 }}>
          <EmployeeSearch
            value={newEmployee}
            onChange={setNewEmployee}
            label="New reviewer"
            placeholder="Search individuals..."
            width="100%"
          />
        </Box>

        {confirmTransfer ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            The new reviewer will be recorded as the author of the existing ratings and comments.
            Continue?
          </Alert>
        ) : (
          newEmployee?.id && (
            <TextField
              label="Reason (required to discard answers)"
              helperText="Required only if you Transfer & Discard. Leave blank to Transfer and keep the previous answers."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 1 }}
            />
          )
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        {confirmTransfer ? (
          <Button variant="contained" onClick={() => submit(true)} disabled={submitting || !newEmployee?.id}>
            {submitting ? <CircularProgress size={18} /> : 'Confirm transfer'}
          </Button>
        ) : (
          <>
            <Tooltip title="Keep the previous reviewer's ratings and comments — the new reviewer becomes their author.">
              <span>
                <Button
                  variant="outlined"
                  onClick={() => setConfirmTransfer(true)}
                  disabled={submitting || !newEmployee?.id}
                >
                  Transfer
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Replace the reviewer and discard the previous answers — the new reviewer starts from a blank matrix. Requires a reason.">
              <span>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => submit(false)}
                  disabled={submitting || !newEmployee?.id || !reason.trim()}
                >
                  Transfer &amp; Discard
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 2 follow-up: Switch reviewer order ----------------------------
// Swaps the order of two not-yet-started Sequential reviewers. `candidates` are
// the eligible siblings to switch with (already filtered to pending future
// steps, excluding the current reviewer). Non-destructive — reason is optional.
const SwitchOrderModal = ({ open, onClose, assignmentId, reviewerId, reviewerName, candidates = [], onChanged }) => {
  const { showAlert } = useAlert();
  const [targetReviewerId, setTargetReviewerId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setTargetReviewerId('');
      setReason('');
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!targetReviewerId) {
      return;
    }
    setSubmitting(true);
    try {
      await skillsApi.switchReviewerOrder(assignmentId, reviewerId, {
        targetReviewerId,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      showAlert('Reviewer order switched', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      showAlert(err.message || 'Failed to switch reviewer order', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Switch order for {reviewerName || 'reviewer'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Swap this reviewer&apos;s position with another not-yet-started reviewer (the currently
          active step is allowed — it stays assigned to whoever takes its position). Reviewers whose
          review is in progress or completed cannot be switched.
        </Typography>
        {candidates.length === 0 ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            There are no other not-yet-started reviewers to switch positions with.
          </Alert>
        ) : (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="switch-order-target-label">Switch position with</InputLabel>
            <Select
              labelId="switch-order-target-label"
              label="Switch position with"
              value={targetReviewerId}
              onChange={(e) => setTargetReviewerId(e.target.value)}
            >
              {candidates.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.fullName || 'Reviewer'}{c.order != null ? ` • Order: ${c.order}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting || !targetReviewerId}>
          {submitting ? <CircularProgress size={18} /> : 'Switch order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 2: Remove reviewer --------------------------------------------
const RemoveReviewerModal = ({ open, onClose, assignmentId, reviewerId, reviewerName, onChanged }) => {
  const { showAlert } = useAlert();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return;
    }
    setSubmitting(true);
    try {
      await skillsApi.removeReviewer(assignmentId, reviewerId, reason.trim());
      showAlert('Reviewer removed', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      showAlert(err.message || 'Failed to remove reviewer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove reviewer {reviewerName || ''}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This removes the reviewer and all of their responses. This cannot be undone (an audit
          record is kept).
        </Alert>
        <TextField
          label="Reason (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={submitting || !reason.trim()}>
          {submitting ? <CircularProgress size={18} /> : 'Remove reviewer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 8: Reopen review ----------------------------------------------
const ReopenReviewModal = ({ open, onClose, reviewerId, onChanged }) => {
  const { showAlert } = useAlert();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await skillsApi.reopenReview(reviewerId, reason.trim());
      showAlert('Review reopened', 'success');
      onChanged?.();
      close();
    } catch (err) {
      showAlert(err.message || 'Failed to reopen review', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Reopen review</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This un-submits the reviewer&apos;s competency review so they can edit their
          skills-matrix selections again. Their existing answers are kept. Any in-progress
          Start/Stop/Continue draft on this step is reset to a draft.
        </Typography>
        <TextField
          label="Reason (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !reason.trim()}
        >
          {submitting ? <CircularProgress size={18} /> : 'Reopen review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 9: Reset review -----------------------------------------------
const ResetReviewModal = ({ open, onClose, reviewerId, onChanged }) => {
  const { showAlert } = useAlert();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return;
    }
    setSubmitting(true);
    try {
      await skillsApi.resetReview(reviewerId, reason.trim());
      showAlert('Review reset', 'success');
      onChanged?.();
      close();
    } catch (err) {
      showAlert(err.message || 'Failed to reset review', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Reset review</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This sets the review back to <strong>Not Started</strong> and permanently deletes the
          reviewer&apos;s answers, along with any in-progress Start/Stop/Continue draft on this step.
          This cannot be undone (an audit record is kept).
        </Alert>
        <TextField
          label="Reason (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !reason.trim()}
        >
          {submitting ? <CircularProgress size={18} /> : 'Reset review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Decision 10: Reopen an interim reviewer's Start/Stop/Continue ----------
const ReopenReviewerActionsModal = ({ open, onClose, reviewerId, reviewerName, onChanged }) => {
  const { showAlert } = useAlert();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await skillsApi.reopenReviewerActions(reviewerId, reason.trim() || undefined);
      showAlert('Start/Stop/Continue reopened', 'success');
      onChanged?.();
      close();
    } catch (err) {
      showAlert(err.message || 'Failed to reopen Start/Stop/Continue', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Reopen Start/Stop/Continue{reviewerName ? ` for ${reviewerName}` : ''}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This reverts this reviewer&apos;s submitted Start/Stop/Continue feedback to a draft so they
          can edit and resubmit it. The text is preserved, their competency review is kept, and the
          next reviewer (who has not started yet) is put back on hold until this step is resubmitted.
        </Typography>
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : 'Reopen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Reviewer-level admin actions (D2 remove/replace, D4, D8, D9, D10) -------
export const ReviewerAdminActions = ({
  assignmentId,
  reviewerId,
  reviewerName,
  isSelfReviewer = false,
  reviewer,
  siblingReviewers = [],
  assignmentStatus,
  // ADR-014 §7: true when the parent session is closed (snapshot close) — the
  // frozen session offers no modification actions (the backend rejects them too).
  sessionLocked = false,
  onChanged,
}) => {
  const canModify = useCanModifyFeedbackSession();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeModal, setActiveModal] = useState(null);

  if (!canModify || sessionLocked) {
    return null;
  }

  const openModal = (name) => {
    setActiveModal(name);
    setAnchorEl(null);
  };

  // ADR-003 Decision 8: a submitted review can be un-submitted so the matrix is
  // editable again. PARALLEL is additionally blocked once the whole assignment is
  // Completed (reopen the Start/Stop/Continue actions first); SEQUENTIAL is limited
  // to the active step (or the finalized chain's last step). The backend enforces
  // the same rules — this just keeps the menu honest.
  const isParallelReviewer = reviewer?.reviewType !== REVIEW_TYPES.SEQUENTIAL;
  const assignmentCompleted = assignmentStatus === ASSIGNMENT_STATUSES.COMPLETED;
  const reopenReviewEligible =
    canReopenReview(reviewer, siblingReviewers) && !(isParallelReviewer && assignmentCompleted);
  const reopenReviewDisabledReason = !reviewer?.completedAt
    ? 'The review must be submitted before it can be reopened.'
    : isParallelReviewer && assignmentCompleted
      ? 'Reopen the Start/Stop/Continue actions before reopening a review on a completed assignment.'
      : 'Only the active reviewer step can be reopened in a Sequential session.';

  // ADR-003 Decision 9: a started/completed review can be reset back to "Not
  // Started" (wiping the reviewer's answers). PARALLEL is blocked once the whole
  // assignment is Completed (reopen the Start/Stop/Continue actions first);
  // SEQUENTIAL is limited to the active step, the step before a not-yet-started
  // active step, or the finalized chain's last step. The backend enforces the
  // same rules — this just keeps the menu honest.
  const hasReviewWork = !!reviewer?.startedAt || !!reviewer?.completedAt;
  const resetReviewEligible =
    canResetReview(reviewer, siblingReviewers) && !(isParallelReviewer && assignmentCompleted);
  const resetReviewDisabledReason = !hasReviewWork
    ? 'The review has not been started, so there is nothing to reset.'
    : isParallelReviewer && assignmentCompleted
      ? 'Reopen the Start/Stop/Continue actions before resetting a review on a completed assignment.'
      : 'Only the active reviewer step (or the step before a not-yet-started active step) can be reset in a Sequential session.';

  // ADR-003 Decision 10: an INTERIM Sequential reviewer's per-step Start/Stop/
  // Continue can be reopened only when it is the step before a not-yet-started
  // active step. The self-assessment has no SSC and the final reviewer's SSC is
  // reopened from the assignee's menu, so this item only renders for Sequential
  // non-self, non-last reviewers. The backend enforces the same rules.
  const reviewerOrders = (siblingReviewers || [])
    .map((r) => r.order)
    .filter((o) => o !== null && o !== undefined);
  const maxReviewerOrder = reviewerOrders.length > 0 ? Math.max(...reviewerOrders) : null;
  const isLastReviewer = reviewer?.order != null && reviewer.order === maxReviewerOrder;
  const showReopenReviewerActions =
    reviewer?.reviewType === REVIEW_TYPES.SEQUENTIAL && !isSelfReviewer && !isLastReviewer;
  const reopenReviewerActionsEligible = canReopenReviewerActions(reviewer, siblingReviewers);
  const reopenReviewerActionsDisabledReason = !reviewer?.feedbackAttachedAt
    ? 'This reviewer has not submitted Start/Stop/Continue feedback yet.'
    : 'Only the step immediately before a not-yet-started active step can have its Start/Stop/Continue reopened.';

  // ADR-003 follow-up: switch order is only offered for Sequential, non-self
  // reviewers. The current reviewer and every candidate must be a not-yet-started
  // future step (mirrors the backend guard). `siblingReviewers` carry the reviewer
  // row id, fullName and order so the dialog can list them.
  const isSequential = reviewer?.reviewType === REVIEW_TYPES.SEQUENTIAL;
  // Exclude self (order 1 — the self-assessment must stay first) and the current
  // reviewer; offer only other not-started steps. The server is authoritative.
  const switchCandidates = (siblingReviewers || []).filter(
    (r) => r.id !== reviewerId && r.order !== 1 && isReorderableNotStartedReviewer(r)
  );
  const selfReorderable = !isSelfReviewer && isReorderableNotStartedReviewer(reviewer);
  const switchEligible = selfReorderable && switchCandidates.length > 0;
  const switchDisabledReason = !selfReorderable
    ? 'Only a reviewer whose review has not started yet (not in progress and not completed) can be reordered.'
    : 'There are no other not-yet-started reviewers to switch positions with.';

  // ADR-003 follow-up: a reviewer whose own review is completed (or whose
  // assignment as a whole is completed) cannot be replaced or removed — doing so
  // would discard a finished review. Reopen/reset the review first. Mirrors the
  // backend `replaceReviewer` / `removeReviewerFromAssignment` guards.
  const reviewCompleted = !!reviewer?.completedAt;
  const replaceRemoveDisabled = reviewCompleted || assignmentCompleted;
  const replaceRemoveDisabledReason = reviewCompleted
    ? 'This reviewer has completed their review. Reopen or reset the review before replacing or removing it.'
    : 'The assignment is completed. Reopen it before replacing or removing a reviewer.';

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Reviewer admin actions">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {/* Reopen review (Decision 8) un-submits the competency review itself;
            eligibility is gated by canReopenReview + the parallel completed gate. */}
        <Tooltip title={reopenReviewEligible ? '' : reopenReviewDisabledReason} placement="left">
          <span>
            <MenuItem
              disabled={!reopenReviewEligible}
              onClick={() => openModal('reopenReview')}
            >
              Reopen review
            </MenuItem>
          </span>
        </Tooltip>
        {/* Reopen Start/Stop/Continue (Decision 10) reverts an interim Sequential
            reviewer's per-step SSC so they can edit/resubmit it. Only rendered for
            Sequential non-self, non-last reviewers; eligibility is gated by
            canReopenReviewerActions. The final reviewer uses the assignee menu. */}
        {showReopenReviewerActions && (
          <Tooltip title={reopenReviewerActionsEligible ? '' : reopenReviewerActionsDisabledReason} placement="left">
            <span>
              <MenuItem
                disabled={!reopenReviewerActionsEligible}
                onClick={() => openModal('reopenReviewerActions')}
              >
                Reopen Start/Stop/Continue
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {/* Reset review (Decision 9) wipes the reviewer's answers and sets the
            step back to Not Started; eligibility is gated by canResetReview + the
            parallel completed gate. */}
        <Tooltip title={resetReviewEligible ? '' : resetReviewDisabledReason} placement="left">
          <span>
            <MenuItem
              disabled={!resetReviewEligible}
              onClick={() => openModal('resetReview')}
              sx={{ color: 'error.main' }}
            >
              Reset review
            </MenuItem>
          </span>
        </Tooltip>
        {/* Switch order is only meaningful for Sequential, non-self reviewers;
            eligibility is gated by isReorderablePendingReviewer + candidates. */}
        {!isSelfReviewer && isSequential && (
          <Tooltip title={switchEligible ? '' : switchDisabledReason} placement="left">
            <span>
              <MenuItem disabled={!switchEligible} onClick={() => openModal('switchOrder')}>
                Switch order
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {/* Replace / Remove stay hidden for the self-assessment (Decision 2
            self-reviewer ban) — the self row is cleaned up via Remove Assignee.
            They are disabled once the reviewer's review (or the whole assignment)
            is completed; the backend enforces the same rules. */}
        {!isSelfReviewer && (
          <Tooltip title={replaceRemoveDisabled ? replaceRemoveDisabledReason : ''} placement="left">
            <span>
              <MenuItem disabled={replaceRemoveDisabled} onClick={() => openModal('replace')}>
                Replace reviewer
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {!isSelfReviewer && (
          <Tooltip title={replaceRemoveDisabled ? replaceRemoveDisabledReason : ''} placement="left">
            <span>
              <MenuItem disabled={replaceRemoveDisabled} onClick={() => openModal('remove')} sx={{ color: 'error.main' }}>
                Remove reviewer
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>

      <ReopenReviewModal
        open={activeModal === 'reopenReview'}
        onClose={() => setActiveModal(null)}
        reviewerId={reviewerId}
        onChanged={onChanged}
      />
      <ResetReviewModal
        open={activeModal === 'resetReview'}
        onClose={() => setActiveModal(null)}
        reviewerId={reviewerId}
        onChanged={onChanged}
      />
      <ReopenReviewerActionsModal
        open={activeModal === 'reopenReviewerActions'}
        onClose={() => setActiveModal(null)}
        reviewerId={reviewerId}
        reviewerName={reviewerName}
        onChanged={onChanged}
      />
      <SwitchOrderModal
        open={activeModal === 'switchOrder'}
        onClose={() => setActiveModal(null)}
        assignmentId={assignmentId}
        reviewerId={reviewerId}
        reviewerName={reviewerName}
        candidates={switchCandidates}
        onChanged={onChanged}
      />
      <ReplaceReviewerModal
        open={activeModal === 'replace'}
        onClose={() => setActiveModal(null)}
        assignmentId={assignmentId}
        reviewerId={reviewerId}
        onChanged={onChanged}
      />
      <RemoveReviewerModal
        open={activeModal === 'remove'}
        onClose={() => setActiveModal(null)}
        assignmentId={assignmentId}
        reviewerId={reviewerId}
        reviewerName={reviewerName}
        onChanged={onChanged}
      />
    </>
  );
};

// --- Decision 6: Add assignee (session header button) -----------------------
// The self-review is created automatically by the backend; other reviewers are
// intentionally NOT selectable here — they are added afterwards per-assignee via
// the "Add reviewer" action. The skills matrix is presented with the same rich
// card list as the "Change skills matrix" flow, scoped to the session's
// configured matrices (useSessionMatrixOptions).
const AddAssigneeModal = ({ open, onClose, sessionId, sessionTemplateId, onChanged }) => {
  const { showAlert } = useAlert();
  const { matrices, getMatrixRoles, loading: matricesLoading } = useSessionMatrixOptions(
    open,
    sessionTemplateId
  );
  const [assignee, setAssignee] = useState(null);
  const [skillsMatrixId, setSkillsMatrixId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setAssignee(null);
      setSkillsMatrixId('');
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!assignee?.id || !skillsMatrixId) {
      return;
    }
    setSubmitting(true);
    try {
      await feedbackApi.addAssignee(sessionId, {
        employeeId: assignee.id,
        skillsMatrixId,
        // Self-review is auto-added server-side; no other reviewers from this view.
        reviewers: [],
      });
      showAlert('Assignee added', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      showAlert(err.message || 'Failed to add assignee', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add assignee</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, mb: 2 }}>
          <EmployeeSearch
            value={assignee}
            onChange={setAssignee}
            label="Assignee (Individual)"
            placeholder="Search individuals..."
            width="100%"
          />
        </Box>
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Skills matrix</Typography>
          {!matricesLoading && matrices.length > 0 && (
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
              {matrices.length} {matrices.length === 1 ? 'matrix' : 'matrices'} available — scroll to see all
            </Typography>
          )}
        </Box>
        {matricesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 1 }}>
            <SkillsMatrixSelectionList
              matrices={matrices}
              selectedMatrixId={skillsMatrixId}
              onSelect={(matrix) => setSkillsMatrixId(matrix.id)}
              {...(getMatrixRoles ? { getMatrixRoles } : {})}
              emptyDescription="There are no skills matrices configured for this session."
            />
          </Box>
        )}
        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 2 }}>
          The self-review is added automatically. Additional reviewers can be added afterwards from
          each assignee&apos;s actions menu.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting || !assignee?.id || !skillsMatrixId}>
          {submitting ? <CircularProgress size={18} /> : 'Add assignee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AddAssigneeButton = ({ sessionId, sessionTemplateId, sessionLocked = false, onChanged }) => {
  const canModify = useCanModifyFeedbackSession();
  const [open, setOpen] = useState(false);
  // ADR-014 §7: no assignees can be added to a closed (frozen) session.
  if (!canModify || sessionLocked) {
    return null;
  }
  return (
    <>
      <HeaderButton onClick={() => setOpen(true)}>
        <FaPlus /> Add Assignee
      </HeaderButton>
      <AddAssigneeModal
        open={open}
        onClose={() => setOpen(false)}
        sessionId={sessionId}
        sessionTemplateId={sessionTemplateId}
        onChanged={onChanged}
      />
    </>
  );
};
